import { BuildInfoProvider, HeaderActionsProvider } from '@concord/ui';
import { createBrowserRouter, Navigate, Outlet } from 'react-router';
import { AuditPage } from '../features/audit/AuditPage';
import { AuthLink } from '../features/auth/AuthLink';
import { SecondStep } from '../features/auth/SecondStep';
import { SignIn } from '../features/auth/SignIn';
import { Welcome } from '../features/auth/Welcome';
import { CasePage } from '../features/case/CasePage';
import { CasesPage } from '../features/cases/CasesPage';
import { ConsoleHome } from '../features/console/ConsoleHome';
import { SearchPage } from '../features/console/SearchPage';
import { AccountPage } from '../features/directory/AccountPage';
import { DirectoryPage } from '../features/directory/DirectoryPage';
import { DocsHandoff } from '../features/docs/DocsHandoff';
import { FeedbackPage } from '../features/feedback/FeedbackPage';
import { FeedbackWidget } from '../features/feedback/FeedbackWidget';
import { HelpButton, HelpProvider } from '../features/help/Help';
import { PatientIntake } from '../features/intake/PatientIntake';
import { StaffIntake } from '../features/intake/StaffIntake';
import { BuildPanelPage } from '../features/panel/BuildPanelPage';
import { PatientHome } from '../features/patient/PatientHome';
import { PlansPage } from '../features/plans/PlansPage';
import { PoolPage } from '../features/pools/PoolPage';
import { PoolsPage } from '../features/pools/PoolsPage';
import { RespondPage } from '../features/respond/RespondPage';
import { SecurityPage } from '../features/security/SecurityPage';
import { BUILD_TEXT, IS_STAGING } from '../lib/build';
import { IdleGuard } from '../lib/idle';
import { PageLoading } from '../lib/loading';
import { RequireRealm, useMe } from '../lib/session';
import { NotFound } from './NotFound';

function Root() {
  const { data: me, isPending } = useMe();
  if (isPending) return null;
  if (me?.realm === 'staff') return <Navigate to="/console" replace />;
  if (me?.realm === 'patient') return <Navigate to="/home" replace />;
  return <Navigate to="/sign-in" replace />;
}

/** Around every screen: the Help button in each header, its drawer, the Feedback button and the loading spinner. */
export function AppChrome() {
  return (
    <HelpProvider>
      <HeaderActionsProvider value={<HelpButton />}>
        <BuildInfoProvider value={BUILD_TEXT}>
          <Outlet />
        </BuildInfoProvider>
      </HeaderActionsProvider>
      {IS_STAGING && (
        <div className="env-banner" role="note">
          <strong>Staging</strong>
          <span> · test site for trying features, not for real patients</span>
        </div>
      )}
      <FeedbackWidget />
      <IdleGuard />
      <PageLoading />
    </HelpProvider>
  );
}

export const routes = [
  { path: '/', element: <Root /> },
  { path: '/sign-in', element: <SignIn realm="patient" /> },
  { path: '/console/sign-in', element: <SignIn realm="staff" /> },
  { path: '/auth/link', element: <AuthLink realm="patient" /> },
  { path: '/console/auth/link', element: <AuthLink realm="staff" /> },
  { path: '/console/verify', element: <SecondStep /> },
  { path: '/welcome/:token', element: <Welcome /> },
  { path: '/home', element: <RequireRealm realm="patient">{(me) => <PatientHome me={me} />}</RequireRealm> },
  {
    path: '/cases/:id/intake/:step?',
    element: <RequireRealm realm="patient">{(me) => <PatientIntake me={me} />}</RequireRealm>,
  },
  { path: '/console', element: <RequireRealm realm="staff">{(me) => <ConsoleHome me={me} />}</RequireRealm> },
  {
    path: '/console/cases/:key',
    element: <RequireRealm realm="staff">{(me) => <CasePage me={me} />}</RequireRealm>,
  },
  {
    path: '/console/cases/:key/panel',
    element: <RequireRealm realm="staff">{(me) => <BuildPanelPage me={me} />}</RequireRealm>,
  },
  { path: '/respond/:token', element: <RespondPage /> },
  {
    path: '/console/cases',
    element: <RequireRealm realm="staff">{(me) => <CasesPage me={me} />}</RequireRealm>,
  },
  {
    path: '/console/panels',
    element: (
      <RequireRealm realm="staff">
        {(me) => <CasesPage me={me} fixedQueue="panel" current="panels" />}
      </RequireRealm>
    ),
  },
  {
    path: '/console/consultations',
    element: (
      <RequireRealm realm="staff">
        {(me) => <CasesPage me={me} fixedQueue="week" current="consultations" />}
      </RequireRealm>
    ),
  },
  {
    path: '/console/search',
    element: <RequireRealm realm="staff">{(me) => <SearchPage me={me} />}</RequireRealm>,
  },
  {
    path: '/console/intake/:id/:step?',
    element: <RequireRealm realm="staff">{(me) => <StaffIntake me={me} />}</RequireRealm>,
  },
  {
    path: '/console/directory/:id',
    element: <RequireRealm realm="staff">{(me) => <AccountPage me={me} />}</RequireRealm>,
  },
  {
    path: '/console/directory',
    element: <RequireRealm realm="staff">{(me) => <DirectoryPage me={me} />}</RequireRealm>,
  },
  {
    path: '/console/docs',
    element: <RequireRealm realm="staff">{() => <DocsHandoff />}</RequireRealm>,
  },
  {
    path: '/console/plans',
    element: <RequireRealm realm="staff">{(me) => <PlansPage me={me} />}</RequireRealm>,
  },
  {
    path: '/console/pools',
    element: <RequireRealm realm="staff">{(me) => <PoolsPage me={me} />}</RequireRealm>,
  },
  {
    path: '/console/pools/:id',
    element: <RequireRealm realm="staff">{(me) => <PoolPage me={me} />}</RequireRealm>,
  },
  {
    path: '/console/security',
    element: <RequireRealm realm="staff">{(me) => <SecurityPage me={me} />}</RequireRealm>,
  },
  {
    path: '/console/audit',
    element: <RequireRealm realm="staff">{(me) => <AuditPage me={me} />}</RequireRealm>,
  },
  {
    path: '/console/feedback',
    element: <RequireRealm realm="staff">{(me) => <FeedbackPage me={me} />}</RequireRealm>,
  },
  { path: '*', element: <NotFound /> },
];

export const router = createBrowserRouter([{ element: <AppChrome />, children: routes }]);
